import ACTIONS from '../constants/actions';
import MESSAGES from '../constants/message-defaults';

/*
 Object that houses overridden messages
 {Object.<string, string>}
 */
let CP_MESSAGES = {};

/**
 * Merges message overrides with defaults
 * @example
 * setMessages({...OVERIDES})
 * @param {Object} overridingMessages
 * @param {Object} defaultMessages
 * @returns {Object} Merged object
 */
export function setMessages(overridingMessages, defaultMessages = MESSAGES) {
  CP_MESSAGES = Object.assign({}, defaultMessages, overridingMessages);
  return CP_MESSAGES;
}

/**
 * Returns message
 * @example
 * // returns 'message value'
 * getMessage('SERVER_ERROR_TITLE')
 * @param {String} key
 * @returns {String} Message value
 */
export function getMessage(key) {
  return CP_MESSAGES[key] || MESSAGES[key];
}

/**
 * A simple template applicator that parses text for /{*}/ and replaces with supplied data
 * @example
 * // returns 'Hi, George Orwell!'
 * applyDataToTemplate('Hi, {person}!', {person: 'George Orwell'})
 * @param {String} template
 * @param {Object} data
 * @returns {String} Message value
 */
export function applyDataToTemplate(template, data) {
  const regex = /{(\w*)}/g;
  return template.replace(regex, (match, key) => {
    return data.hasOwnProperty(key) ? data[key] : '';
  });
}

/**
 * A collection of methods that respond to specific error combinations
 * @namespace
 * @property {Function} noPlanAvailable - Returns not eligible content and action
 * @property {Function} partiallyEligible - Returns partially eligible content and action
 * @property {Function} longConnection - Returns long connection content and action
 *
 * All messages follow the following definitions:
 * @typedef {Object} Message
 * @property {String} action
 * @property {String} title
 * @property {String} message
 *
 * @param {String} action
 * @param {String} fuel
 * @param {Boolean} isBusiness
 * @returns {Message}
 */

const errorMethods = {
  noPlanAvailable: (action) => {
    return {
      action,
      title: getMessage('NOT_ELIGIBLE_TITLE'),
      message: getMessage('NOT_ELIGIBLE_MESSAGE'),
    };
  },

  partiallyEligible: (action, fuel) => {
    return {
      action,
      title: getMessage('PARTIALLY_ELIGIBLE_TITLE'),
      message: applyDataToTemplate(getMessage('PARTIALLY_ELIGIBLE_MESSAGE'), {
        fuel,
      }),
    };
  },

  longConnection: (action, fuel, isBusiness) => {
    return {
      action,
      title: getMessage('LONG_CONNECTION_TIME_TITLE'),
      // eslint-disable-next-line
      message:
        isBusiness === true
          ? getMessage('LONG_CONNECTION_TIME_BUSINESS_MESSAGE')
          : getMessage('LONG_CONNECTION_TIME_RESIDENTIAL_MESSAGE'),
    };
  },

  noSolarAvailable: (action) => {
    return {
      action,
      title: getMessage('NO_SOLAR_TITLE'),
      message: getMessage('NO_SOLAR_MESSAGE'),
    };
  },

  embeddedNetworkNoEligibleFuel: (action) => {
    return {
      action,
      title: getMessage('EMBEDDEDNETWORK_DUAL_TITLE'),
      message: getMessage('EMBEDDEDNETWORK_DUAL_MESSAGE'),
    };
  },

  embeddedNetworkPartialEligible: (action, fuel) => {
    const titleKey = fuel === 'electricity' ? 'EMBEDDED_ELEC_TITLE' : 'UNMETERED_GAS_TITLE';
    const messageKey = fuel === 'electricity' ? 'EMBEDDED_ELEC_MESSAGE' : 'UNMETERED_GAS_MESSAGE';
    return {
      action,
      title: getMessage(titleKey),
      message: applyDataToTemplate(getMessage(messageKey), {
        fuel,
      }),
    };
  },
};

/**
 * @typedef {Array.<Array.<string>>} errorScenarios
 * @property {RegEx} regex to test against concatenated error values string,
 * an example which is 0301 === (elecCode + gasCode)
 * @property {string} name of function in errorMethods
 * @property {string} property of ACTIONS
 * @property {string}.oneOf['gas', 'electricity'] fuelType
 */

/*

 STATUS CODES:
 01: FULLY_ELIGIBLE
 02: PARTIALLY_ELIGIBLE
 03: NOT_ELIGIBLE
 04: UNKNOWN
 05: LONG_CONNECTION_TIME

 */

const errorScenarios = [
  /*
     NOT_ELIGIBLE (plan not available) Codes Regex.  Regex matches:
     1. customer wants both fuels: 0303
     2. customer wants only gas: _03
     3: customer wants only elec: 03_
     */
  [/^(03|_)(_|03)$/, 'noPlanAvailable', ACTIONS.HIDE_PLANS, ''],

  /*
     (GAS) PARTIALLY_ELIGIBLE Codes. Regex matches:
     1. and customer wants both fuels (Regex Group 1): 0103, 0403
     */
  [/^0(1|4)03$/, 'partiallyEligible', ACTIONS.HIDE_PLANS_GAS, 'gas'],

  /*
     (ELEC) PARTIALLY_ELIGIBLE Codes. Regex matches:
     1. and customer wants both fuels (Regex Group 1): 0301, 0304
     */
  [/^030(1|4)$/, 'partiallyEligible', ACTIONS.HIDE_PLANS_ELECTRICITY, 'electricity'],

  /*
     LONG_CONNECTION_TIME Codes (occurs with elec only, see: AG-520). Regex matches:
     1. and no other fuel = hide plans: 05_, 0503
     */
  [/^05(_|03)$/, 'longConnection', ACTIONS.HIDE_PLANS, ''],

  /*
     LONG_CONNECTION_TIME Codes (occurs with elec only, see: AG-520). Regex matches:
     1. and truthy value for gas, so we just hide elec: 0501, 0502, 0504
     */
  [/^05(01|02|04)$/, 'longConnection', ACTIONS.HIDE_PLANS_ELECTRICITY, 'electricity'],

  /*
   (GAS) PARTIALLY_ELIGIBLE Codes. Regex matches:
   1. and customer wants both fuels (Regex Group 1): 0103, 0403
   */
  [/^0(1|4)0(7|8)$/, 'embeddedNetworkPartialEligible', ACTIONS.HIDE_PLANS_GAS, 'gas'],

  /*
     (ELEC) PARTIALLY_ELIGIBLE Codes. Regex matches:
     1. and customer wants both fuels (Regex Group 1): 0301, 0304
     */
  [/^090(1|4)$/, 'embeddedNetworkPartialEligible', ACTIONS.HIDE_PLANS_ELECTRICITY, 'electricity'],

  /*
  NOT_ELIGIBLE Embedded network Codes Regex.  Regex matches:
  1. customer wants both fuels: 0907 or 0908
  */
  [/^09(07|08)$/, 'embeddedNetworkNoEligibleFuel', ACTIONS.HIDE_PLANS, ''],

  /*
   1. customer wants both gas and the gas is either unMetered or Gascooktop: _07 or _08
   */
  [/^_(07|08)$/, 'embeddedNetworkPartialEligible', ACTIONS.HIDE_PLANS, 'gas'],

  /*
     1. customer wants electricity and the address is embedded network: 09_
     */
  [/^(09)_$/, 'embeddedNetworkPartialEligible', ACTIONS.HIDE_PLANS, 'electricity'],
];

/**
 * Processes error codes and returns message
 * @example
 * // returns { ...Message }
 getMessage(
 {
     electricity: '01',
     gas: '03'
 },
 {
     electricity: true,
     gas: true
 },
 false,
 false
 )
 * @param {Object} fuelEligibilityStatus object from Plans API
 * @param {Object} servicesRequired
 * @param {Boolean} servicesRequired.gas
 * @param {Boolean} servicesRequired.electricity
 * @param {Boolean} isBusiness
 * @param {Boolean} hasSolarPanels
 * @returns {Message}
 */

export function getPlanEligibilityError(
  { electricity = undefined, gas = undefined, state = undefined } = {},
  servicesRequired = {},
  isBusiness = false,
  hasSolarPanels = false,
  plans = [],
) {
  // if we have a code for the fuel, and the user has selected that fuel, we can proceed with error checking
  // otherwise assign it _
  electricity =
    electricity && typeof electricity !== 'undefined' && servicesRequired.electricity === true
      ? electricity
      : '_';
  gas = gas && typeof gas !== 'undefined' && servicesRequired.gas === true ? gas : '_';

  // concatenate codes elec|gas
  const errorCode = electricity.concat(gas);

  let message;

  if (errorCode === '__') {
    return message;
  }

  /**
   * WA ONLY. Hide the 'no electricty' error message for WA
   */
  if (errorCode === '0304' && state === 'WA') {
    return message;
  }

  for (let errorScenario of errorScenarios) {
    if (errorScenario[0].test(errorCode) === true) {
      message = errorMethods[errorScenario[1]](
        errorScenario[2],
        errorScenario[3],
        isBusiness,
        hasSolarPanels,
      );
      break;
    }
  }

  /**
   * Display an error message if the user has solar panels but no solar fit is
   * available in the plans. However if an error has already occurred then the first
   * error takes priority.
   */
  if (!message && hasSolarPanels) {
    if (!hasSolar(plans)) {
      message = errorMethods['noSolarAvailable'](ACTIONS.NO_SOLAR);
    }
  }

  // if there's a message, we know there's an error
  // so let's create the data param for the form error
  if (message) {
    message.trackingData = getFormTrackingData(message.title, [electricity, gas]);
  }

  return message;
}

/**
 * Check if solar fit is available in the plans.
 * @param {array} plans - array of plans
 * @returns {boolean}
 */
function hasSolar(plans) {
  return plans.filter((plan) => plan.fuel.electricity.solarFit !== null).length > 0;
}

/** Returns a Message object from a server error
 * @param {Object} err
 * @param {Boolean} isBusiness
 * @returns {Message}
 */
export function getServerError(err = {}, isBusiness = false) {
  // the default message for business and residential are different
  const messageType = isBusiness ? 'BUSINESS' : 'RESIDENTIAL';
  /*
        For some errors the server returns a key in the message,
        e.g., { message: 'INVALID_ADDRESS_ID' }
        This is to cover the case where, for example, the API receives
        an error from SAP but the error message is too cryptic
        for the user.
        The message acts as a key, so if we find it in our message object,
        we'll use it. Otherwise revert to the default message.
    */
  const title = getMessage(`${err.message}_TITLE`) || getMessage('SERVER_ERROR_TITLE');
  const message =
    getMessage(`${err.message}_MESSAGE`) || getMessage(`SERVER_ERROR_MESSAGE_${messageType}`);

  return {
    action: ACTIONS.HIDE_PLANS,
    title,
    message,
    trackingData: getFormTrackingData(err.message, [err.code]),
  };
}

/**
 * Create tracking data message for 'form error'
 * @param {array} plans - array of plans
 * @returns {string}
 */
export function getFormTrackingData(description, errorCodes = []) {
  errorCodes = errorCodes.filter((item) => item !== '_');

  return `${description}:${errorCodes.join(',')}`;
}
